import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Select,
  useToast,
  Text,
  VStack,
  HStack,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useStores } from '../../store/store_context';
import UserModal from '../user_modal';
import PanelModal from '../panel_modal';
import {
  getUserById,
  addPanelToUser,
  getDocByUserId,
  getAllUsersForUsername,
  getAllPanelsById,
  updateUserById,
  updatePanelById,
} from '../../store/api_funcs';
import RolesSearch from '../roles_autocomplete';
import PanelsSearch from '../panels_autocomplete';
import PanelEditForm from '../panel_forms/panel_edit_form';

// Валидационная схема для Formik и Yup
const validationSchema = Yup.object().shape({
  name: Yup.string().required('Обязательное поле'),
  username: Yup.string().required('Обязательное поле'),
  hashed_password: Yup.string().required('Обязательное поле'),
  description: Yup.string(),
  phone: Yup.string().required('Обязательное поле'),
  language: Yup.string().required('Обязательное поле'),
  customer_type: Yup.string().required('Обязательное поле'),
  contract_number: Yup.string().required('Обязательное поле'), // Добавлено новое поле для валидации
});

// Компонент формы для просмотра и изменения данных пользователя
const UserEditForm = ({
  user_id,
  onSubmit,
  updateUsersTable,
  getTablePanels,
  panels,
  rolesForUserForm = [],
  myself = false,
}) => {
  const { pageStore } = useStores();
  const toast = useToast();
  const [initialValues, setInititalValues] = useState();
  const [roleId, setRoleId] = useState(1);
  const [myUsers, setMyUsers] = useState([]);
  const [userPanels, setUserPanels] = useState([]);
  const [panelId, setPanelId] = useState(0);

  const getUserInfo = async () => {
    const userData = await getUserById(user_id, pageStore.token);
    setInititalValues(userData);
    if (userData.role_id) {
      setRoleId(userData.role_id);
    }
  };
  const getMyUsers = async () => {
    setMyUsers(
      await getAllUsersForUsername(pageStore.token, initialValues?.username)
    );
  };
  const getUserPanels = async () => {
    setUserPanels(await getAllPanelsById(user_id, pageStore.token));
  };

  useEffect(() => {
    getUserInfo();
  }, []);
  useEffect(() => {
    getMyUsers();
  }, [initialValues]);
  useEffect(() => {
    getUserPanels();
  }, [user_id]);

  const handleSubmit = async (values, actions) => {
    const restructeredValues = { ...values, role_id: roleId };
    try {
      await onSubmit(user_id, restructeredValues, pageStore.token);
      if (myself) await pageStore.getMe(pageStore.token);
      toast({
        title: 'Данные успешно сохранены.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Ошибка при отправке данных:', error);
      toast({
        title: 'Ошибка',
        description: 'Что-то пошло не так. Пожалуйста, попробуйте снова.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      await updateUsersTable();
      actions.setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched }) => (
        <Form>
          <Field name="name">
            {({ field }) => (
              <FormControl isInvalid={errors.name && touched.name}>
                <FormLabel htmlFor="name">ФИО:</FormLabel>
                <Input {...field} id="name" />
              </FormControl>
            )}
          </Field>

          <Field name="username">
            {({ field }) => (
              <FormControl isInvalid={errors.username && touched.username}>
                <FormLabel htmlFor="username">Никнейм:</FormLabel>
                <Input {...field} id="username" />
              </FormControl>
            )}
          </Field>

          <Field name="hashed_password">
            {({ field }) => (
              <FormControl
                isInvalid={errors.hashed_password && touched.hashed_password}
              >
                <FormLabel htmlFor="hashed_password">Пароль:</FormLabel>
                <Input {...field} id="hashed_password" />
              </FormControl>
            )}
          </Field>

          <Field name="description">
            {({ field }) => (
              <FormControl
                isInvalid={errors.description && touched.description}
              >
                <FormLabel htmlFor="description">Краткое описание:</FormLabel>
                <Textarea {...field} id="description" />
              </FormControl>
            )}
          </Field>

          <Field name="phone">
            {({ field }) => (
              <FormControl isInvalid={errors.phone && touched.phone}>
                <FormLabel htmlFor="phone">Телефон:</FormLabel>
                <Input {...field} id="phone" />
              </FormControl>
            )}
          </Field>

          {pageStore.add_roles ? (
            <RolesSearch
              items={rolesForUserForm.map(elem => {
                return { id: elem.role_id, name: elem.role_name };
              })}
              setResult={setRoleId}
            />
          ) : null}

          <Field name="language">
            {({ field }) => (
              <FormControl isInvalid={errors.language && touched.language}>
                <FormLabel htmlFor="language">Язык:</FormLabel>
                <Input {...field} id="language" />
              </FormControl>
            )}
          </Field>

          <Field name="customer_type">
            {({ field }) => (
              <FormControl
                isInvalid={errors.customer_type && touched.customer_type}
              >
                <FormLabel htmlFor="customer_type">Тип пользователя:</FormLabel>
                <Select {...field} id="customer_type">
                  <option value="УК" style={{ color: 'black' }}>
                    УК
                  </option>
                  <option value="ТСЖ" style={{ color: 'black' }}>
                    ТСЖ
                  </option>
                  <option value="ЖСК" style={{ color: 'black' }}>
                    ЖСК
                  </option>
                  <option value="БЦ" style={{ color: 'black' }}>
                    БЦ
                  </option>
                  <option value="ТЦ" style={{ color: 'black' }}>
                    ТЦ
                  </option>
                  <option value="Бизнес Партнер" style={{ color: 'black' }}>
                    Бизнес Партнер
                  </option>
                  <option value="МСК" style={{ color: 'black' }}>
                    МСК
                  </option>
                  <option
                    value="Региональный Партнер"
                    style={{ color: 'black' }}
                  >
                    Региональный Партнер
                  </option>
                </Select>
              </FormControl>
            )}
          </Field>

          {/* Новое поле для contract_number */}
          <Field name="contract_number">
            {({ field }) => (
              <FormControl
                isInvalid={errors.contract_number && touched.contract_number}
              >
                <FormLabel htmlFor="contract_number">
                  Номер контракта:
                </FormLabel>
                <Input {...field} id="contract_number" />
              </FormControl>
            )}
          </Field>

          <Button mt={4} colorScheme="blue" type="submit">
            Сохранить
          </Button>
          <VStack align={'flex-start'}>
            {pageStore.add_panels && (
              <>
                <Text fontSize={16} color={'gray'}>
                  Чтобы назначить пользователя к панели начните печатать ее
                  адрес или номер и выбрав панель нажмите кнопку добавить
                </Text>
                <PanelsSearch
                  setResult={setPanelId}
                  items={panels.map(elem => {
                    return {
                      id: elem.panel_id,
                      name: elem.adress,
                      number: elem.fabric_number,
                    };
                  })}
                />
                <Button
                  colorScheme="blue"
                  onClick={async () => {
                    try {
                      await addPanelToUser(panelId, user_id, pageStore.token);
                      toast({
                        title: 'Данные успешно сохранены.',
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                      });
                      await getTablePanels();
                    } catch (error) {
                      console.error('Ошибка при отправке данных:', error);
                      toast({
                        title: 'Ошибка',
                        description:
                          'Что-то пошло не так. Пожалуйста, попробуйте снова.',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                      });
                    }
                  }}
                >
                  Добавить
                </Button>
              </>
            )}
            <Button
              onClick={() => {
                getDocByUserId(user_id);
              }}
            >
              Выгрузить данные
            </Button>
          </VStack>
          {myUsers.length >= 1 ? (
            <VStack width={'100%'} paddingY={'25px'}>
              <Text alignSelf={'flex-start'} fontSize={24}>
                Связанные пользователи:
              </Text>
              {myUsers.map(elem => {
                return (
                  <HStack width={'100%'} justify={'space-between'}>
                    <Text>
                      {elem.username} {elem.name}
                    </Text>
                    <UserModal>
                      <UserEditForm
                        user_id={elem.user_id}
                        updateUsersTable={updateUsersTable}
                        onSubmit={updateUserById}
                        rolesForUserForm={rolesForUserForm}
                        panels={panels}
                      />
                    </UserModal>
                  </HStack>
                );
              })}
            </VStack>
          ) : null}
          {userPanels.length >= 1 ? (
            <VStack width={'100%'} paddingY={'25px'}>
              <Text alignSelf={'flex-start'} fontSize={24}>
                Связанные панели:
              </Text>
              {userPanels.map(elem => {
                return (
                  <HStack width={'100%'} justify={'space-between'}>
                    <Text>
                      {elem.fabric_number} {elem.adress}
                    </Text>
                    <PanelModal>
                      <PanelEditForm
                        panel_id={elem.panel_id}
                        updatePanelsTable={getTablePanels}
                        onSubmit={updatePanelById}
                        getTableUsers={updateUsersTable}
                        panels={panels}
                      />
                    </PanelModal>
                  </HStack>
                );
              })}
            </VStack>
          ) : null}
        </Form>
      )}
    </Formik>
  );
};

export default UserEditForm;
